import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIsPublishInProgress, getIsPublishFailed } from '../../../redux/selectors/publishSelectors';
import { getIsUserJitad } from '../../../redux/selectors/authSelectors';
import { getIsSavePermanentlyDisabled } from '../../../redux/selectors/saveDataSelectors';
// @ts-expect-error Untyped module
import { getHasSaveError } from '../../../redux/selectors/editingInfoSelectors';
import usageTracker from '../../../utils/UsageTracker';
// @ts-expect-error Untyped module
import cannotPublishOnDomainReasons from '../../../constants/cannotPublishOnDomainReasons';
// @ts-expect-error Untyped module
import { formatErrorMessage } from '../../../utils/validationUtils';
import { TimeIcon } from 'foundations-assets';
import { getHardSavedContentModelIsScheduled } from '../../../redux/selectors/hardSavedContentModelSelectors';
import { getContentModelPageExpiryEnabled } from '../../../redux/selectors/baseContentModelSelectors';
import { PublishActionModalNames } from '../../publishing/types/publishButtonTypes';
import useContentCrmObject from './useContentCrmObject';
// @ts-expect-error Untyped module
import { getContextActiveDomainAsMap } from 'ContentEditorUI/redux/selectors/contentSchemaSelectors';
import I18n from 'I18n';
import { getPublishNoun } from 'ContentEditorUI/redux/selectors/publishSelectors';
import ContentRoutes from 'ContentUtils/Routes';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useEditorPublishProps = ({
  canSchedule,
  lockedPublishButtonProps: {
    isLocked
  } = {},
  extraDropdownActions,
  unpublishEnabled,
  publishEnabled,
  cannotPublishOnDomainReason,
  errors,
  warnings,
  updateFocusedItemAction
}) => {
  const dispatch = useDispatch();
  const [cancelScheduleModalType, setCancelScheduleModalType] = useState('schedulePublish');
  const [openModalName, setOpenModalName] = useState(null);
  const openModal = modalName => setOpenModalName(modalName);
  const closeModal = () => setOpenModalName(null);
  const {
    isContentCrmObjectScheduled,
    isContentCrmObjectScheduledToBeUnpublished
  } = useContentCrmObject();
  const isScheduled = useSelector(getHardSavedContentModelIsScheduled);
  const isPageExpiryEnabled = useSelector(getContentModelPageExpiryEnabled);
  const isSavePermanentlyDisabled = useSelector(getIsSavePermanentlyDisabled);
  const hasSaveError = useSelector(getHasSaveError);
  const isPublishEnabled = publishEnabled && !isSavePermanentlyDisabled && !hasSaveError;
  const getDropdownActions = useCallback((forApprovals = false) => {
    const actions = [];
    if (!forApprovals && !isLocked) {
      if (canSchedule) {
        actions.push({
          id: 'publishing-options-button',
          label: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(TimeIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "editor.publishing.schedulingOptions"
            })]
          }),
          onClick: () => {
            usageTracker.trackEditorInteraction({
              action: 'Publish Dropdown Action',
              event: 'clicked-action--publish-options'
            });
            openModal(PublishActionModalNames.SCHEDULING_OPTIONS);
          }
        });
      }
      if (isContentCrmObjectScheduled || isScheduled) {
        actions.push({
          id: 'cancel-scheduled-publish-button',
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "editor.publishing.cancelScheduledPublish"
          }),
          onClick: () => {
            usageTracker.trackEditorInteraction({
              action: 'Publish Dropdown Action',
              event: 'clicked-action--cancel-scheduled-publish'
            });
            setCancelScheduleModalType('schedulePublish');
            openModal(PublishActionModalNames.CANCEL_SCHEDULE);
          }
        });
      }
      if (isContentCrmObjectScheduledToBeUnpublished || isPageExpiryEnabled) {
        actions.push({
          id: 'cancel-scheduled-unpublish-button',
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "editor.publishing.cancelScheduledUnpublish"
          }),
          onClick: () => {
            usageTracker.trackEditorInteraction({
              action: 'Publish Dropdown Action',
              event: 'clicked-action--cancel-scheduled-unpublish'
            });
            setCancelScheduleModalType('scheduleUnpublish');
            openModal(PublishActionModalNames.CANCEL_SCHEDULE);
          }
        });
      }
      if (extraDropdownActions) {
        actions.push(...extraDropdownActions);
      }
    }
    if (unpublishEnabled) {
      actions.push({
        id: 'unpublish-button',
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "header.unpublishButton"
        }),
        onClick: () => {
          usageTracker.trackEditorInteraction({
            action: 'Publish Dropdown Action',
            event: 'clicked-action--unpublish'
          });
          openModal(PublishActionModalNames.UNPUBLISH_CONFIRM);
        }
      });
    }
    return actions;
  }, [canSchedule, extraDropdownActions, isContentCrmObjectScheduled, isContentCrmObjectScheduledToBeUnpublished, isLocked, isPageExpiryEnabled, isScheduled, unpublishEnabled]);
  const isUserJitad = useSelector(getIsUserJitad);
  const handleMouseEnterButton = useCallback(() => {
    if (!isPublishEnabled) {
      if (cannotPublishOnDomainReason === cannotPublishOnDomainReasons.NO_DOMAIN_PERMISSION) {
        usageTracker.trackEditorInteraction({
          action: 'Header Action',
          event: 'hovered-publish-button-with-permission-error'
        });
      } else if (cannotPublishOnDomainReason === cannotPublishOnDomainReasons.USER_HAS_NO_PUBLISH_ACCESS && !isUserJitad) {
        usageTracker.trackEditorInteraction({
          action: 'Approvals Action',
          event: 'viewContentEditorAccessRequestTooltip'
        });
      }
    }
  }, [cannotPublishOnDomainReason, isPublishEnabled, isUserJitad]);
  const contextActiveDomain = useSelector(getContextActiveDomainAsMap);
  const publishNoun = useSelector(getPublishNoun);
  const {
    publishErrors,
    publishWarnings
  } = useMemo(() => {
    if (isLocked) {
      // hides the issues tooltip when publish is locked
      return {
        publishErrors: [],
        publishWarnings: []
      };
    }
    if (cannotPublishOnDomainReason) {
      errors.push({
        message: `publishingOptions.baymaxNotAuthorizedReasons.${cannotPublishOnDomainReason}`,
        data: {
          domain: contextActiveDomain.get('domain'),
          noun: I18n.text(publishNoun),
          domainManagerUrl: ContentRoutes.domainManager(),
          knowledgeArticleUrl: 'https://knowledge.hubspot.com/articles/kcs_article/cos-general/connect-a-domain-to-hubspot'
        },
        useHtml: true
      });
    }
    return {
      publishErrors: errors.map(formatErrorMessage),
      publishWarnings: warnings.map(formatErrorMessage)
    };
  }, [cannotPublishOnDomainReason, contextActiveDomain, errors, isLocked, publishNoun, warnings]);
  const isPublishInProgress = useSelector(getIsPublishInProgress);
  const loadingFailed = useSelector(getIsPublishFailed);
  return useMemo(() => ({
    publishDropdownActions: getDropdownActions(),
    approvalDropdownActions: getDropdownActions(true),
    isPublishEnabled,
    isPublishInProgress,
    loadingFailed,
    publishErrors,
    publishWarnings,
    errorsAndWarningsCount: publishErrors.length + publishWarnings.length,
    onIssueClick: ({
      target,
      onClickData
    }) => {
      if (!target) return;
      dispatch(updateFocusedItemAction(target, onClickData));
    },
    handleMouseEnterButton,
    handleTrackOnDropdownCompleteAction: () => {
      usageTracker.trackEditorInteraction({
        action: 'Publish Dropdown Action',
        event: 'opened-publish-actions-dropdown'
      });
    },
    openModalName,
    openModal,
    closeModal,
    cancelScheduleModalType,
    setCancelScheduleModalType
  }), [getDropdownActions, isPublishEnabled, isPublishInProgress, loadingFailed, publishErrors, publishWarnings, handleMouseEnterButton, openModalName, cancelScheduleModalType, dispatch, updateFocusedItemAction]);
};
export default useEditorPublishProps;